"use client";

import { useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import {
  motion,
  useInView,
  useScroll,
  useTransform,
  useReducedMotion,
} from "framer-motion";
import {
  FadeInUp,
  ScaleIn,
  StaggerChildren,
  StaggerItem,
} from "@/components/AnimatedSection";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

/* ─── Constants ─── */
const BRAND_RED = "#E11D24";

/* ─── Neural Network Background ─── */
function NeuralNetworkBg() {
  const nodes = [
    { cx: 80, cy: 120 },
    { cx: 220, cy: 60 },
    { cx: 360, cy: 140 },
    { cx: 500, cy: 50 },
    { cx: 640, cy: 130 },
    { cx: 150, cy: 240 },
    { cx: 310, cy: 280 },
    { cx: 470, cy: 220 },
    { cx: 600, cy: 300 },
    { cx: 100, cy: 360 },
    { cx: 260, cy: 400 },
    { cx: 420, cy: 370 },
    { cx: 560, cy: 420 },
    { cx: 700, cy: 360 },
  ];

  const connections = [
    [0, 1], [1, 2], [2, 3], [3, 4],
    [0, 5], [1, 5], [2, 6], [3, 7], [4, 8],
    [5, 6], [6, 7], [7, 8],
    [5, 9], [6, 10], [7, 11], [8, 12],
    [9, 10], [10, 11], [11, 12], [12, 13],
    [9, 11], [10, 12], [11, 13],
  ];

  return (
    <svg
      className="absolute inset-0 w-full h-full opacity-[0.07]"
      viewBox="0 0 780 480"
      preserveAspectRatio="xMidYMid slice"
    >
      {connections.map(([a, b], i) => (
        <motion.line
          key={`conn-${i}`}
          x1={nodes[a].cx}
          y1={nodes[a].cy}
          x2={nodes[b].cx}
          y2={nodes[b].cy}
          stroke={BRAND_RED}
          strokeWidth="1"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 0.6 }}
          transition={{ duration: 1.5, delay: i * 0.05 }}
        />
      ))}
      {nodes.map((node, i) => (
        <motion.circle
          key={`node-${i}`}
          cx={node.cx}
          cy={node.cy}
          r="4"
          fill={BRAND_RED}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: [0.4, 1, 0.4] }}
          transition={{
            scale: { duration: 0.4, delay: i * 0.08 },
            opacity: { duration: 3, repeat: Infinity, delay: i * 0.2 },
          }}
        />
      ))}
    </svg>
  );
}

/* ─── Floating Chat Bubbles ─── */
function FloatingBubbles() {
  const bubbles = [
    { text: "Hi, how can I help?", x: "8%", y: "20%", delay: 0 },
    { text: "Tell me about pricing", x: "78%", y: "30%", delay: 1.5 },
    { text: "Analyzing your query...", x: "15%", y: "65%", delay: 3 },
    { text: "Here are 3 options", x: "72%", y: "70%", delay: 4.5 },
  ];

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {bubbles.map((b, i) => (
        <motion.div
          key={i}
          className="absolute px-4 py-2 rounded-2xl text-xs font-medium border border-[#E11D24]/20 bg-surface-light/50 text-foreground/30 backdrop-blur-sm"
          style={{ left: b.x, top: b.y }}
          initial={{ opacity: 0, y: 20, scale: 0.8 }}
          animate={{
            opacity: [0, 0.6, 0.6, 0],
            y: [20, 0, 0, -10],
            scale: [0.8, 1, 1, 0.9],
          }}
          transition={{
            duration: 6,
            delay: b.delay,
            repeat: Infinity,
            repeatDelay: 6,
          }}
        >
          {b.text}
        </motion.div>
      ))}
    </div>
  );
}

/* ─── Pulsing Node for Timeline ─── */
function PulsingNode({ active, color }: { active: boolean; color: string }) {
  return (
    <div className="relative flex items-center justify-center w-5 h-5 flex-shrink-0">
      {active && (
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{ backgroundColor: color }}
          animate={{ scale: [1, 2, 1], opacity: [0.4, 0, 0.4] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      )}
      <div
        className="w-3 h-3 rounded-full border-2"
        style={{
          borderColor: color,
          backgroundColor: active ? color : "transparent",
        }}
      />
    </div>
  );
}

/* ─── Timeline Step ─── */
function TimelineStep({
  step,
  desc,
  time,
  index,
  isLast,
}: {
  step: string;
  desc: string;
  time: string;
  index: number;
  isLast: boolean;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <motion.div
      ref={ref}
      className="relative flex gap-5"
      initial={{ opacity: 0, x: -30 }}
      animate={isInView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
    >
      {/* Node + vertical line */}
      <div className="flex flex-col items-center">
        <PulsingNode active={isInView} color={BRAND_RED} />
        {!isLast && (
          <motion.div
            className="w-px flex-1 mt-1"
            style={{ backgroundColor: `${BRAND_RED}30` }}
            initial={{ scaleY: 0 }}
            animate={isInView ? { scaleY: 1 } : {}}
            transition={{ duration: 0.8, delay: index * 0.15 + 0.3 }}
            // eslint-disable-next-line react/style-prop-object
          />
        )}
      </div>

      {/* Content */}
      <div className="pb-10">
        <span
          className="inline-block text-[11px] font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-3"
          style={{ backgroundColor: `${BRAND_RED}12`, color: BRAND_RED }}
        >
          {time}
        </span>
        <h4 className="text-lg font-bold font-ui text-foreground mb-1.5">
          {step}
        </h4>
        <p className="text-foreground/50 font-body text-sm leading-relaxed max-w-md">
          {desc}
        </p>
      </div>
    </motion.div>
  );
}

/* ─── Service Data ─── */
const whatWeNeed = [
  {
    item: "Use Case / Goal",
    desc: "What should the chatbot do? Lead intake, customer support, internal knowledge, sales automation?",
  },
  {
    item: "Chatbot Type",
    desc: "Conversational assistant, intake flow, FAQ bot, or multi-step agent with tools and actions.",
  },
  {
    item: "Knowledge Base",
    desc: "Docs, PDFs, CSVs, or website URLs the bot should learn from. We handle ingestion and chunking.",
  },
  {
    item: "Platform / Integration",
    desc: "Where does it live? Website widget, WhatsApp, Slack, API endpoint, or all of the above.",
  },
  {
    item: "Tone & Personality",
    desc: "Professional, friendly, technical, playful? We tune the system prompt to match your brand voice.",
  },
];

const process = [
  {
    step: "Scoping & Architecture",
    desc: "We map your use case, define the conversation flow, plan RAG pipeline architecture, and select the right models.",
    time: "Week 1",
  },
  {
    step: "Knowledge Setup",
    desc: "Ingest your documents into vector storage, build semantic search, set up hybrid retrieval with relevance tuning.",
    time: "Week 2-3",
  },
  {
    step: "Bot Development",
    desc: "Build the conversational engine with intent classification, multi-step flows, tool integrations, and guardrails.",
    time: "Week 3-5",
  },
  {
    step: "Testing & Tuning",
    desc: "End-to-end testing across scenarios, prompt optimization, hallucination reduction, and edge case handling.",
    time: "Week 5-6",
  },
  {
    step: "Deploy & Monitor",
    desc: "Production deployment with analytics dashboard, conversation logging, and ongoing performance monitoring.",
    time: "Week 6",
  },
];

const capabilities = [
  {
    title: "RAG-Powered Knowledge",
    desc: "Hybrid vector + keyword search retrieves precise answers from your docs, not hallucinations.",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
      </svg>
    ),
  },
  {
    title: "Multi-Platform Deploy",
    desc: "One bot, everywhere. Website widget, WhatsApp, Slack, Discord, or custom API endpoints.",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
      </svg>
    ),
  },
  {
    title: "Natural Language Understanding",
    desc: "LLM-powered intent classification handles ambiguity, typos, and complex multi-part questions.",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
      </svg>
    ),
  },
  {
    title: "Lead Qualification",
    desc: "Structured intake flows that collect requirements, qualify leads, and route to your team via Slack or email.",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
      </svg>
    ),
  },
  {
    title: "Analytics & Monitoring",
    desc: "Track conversations, intent distribution, drop-off points, and response quality in real time.",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
      </svg>
    ),
  },
  {
    title: "Admin Dashboard",
    desc: "Manage knowledge base, review conversations, update prompts, and ingest new documents without code.",
    icon: (
      <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

const deliverables = [
  "Deployed chatbot (widget or API)",
  "Admin dashboard",
  "Knowledge base pipeline",
  "Conversation analytics",
  "Integration setup (WhatsApp, Slack, etc.)",
  "Documentation & training",
];

const techStack = [
  "OpenAI GPT-4o",
  "Supabase pgvector",
  "Next.js API Routes",
  "RAG Pipeline",
  "Hybrid Search",
  "Framer Motion",
  "Slack API",
  "WhatsApp API",
];

/* ─── Page ─── */
export default function AIServicePage() {
  const heroRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <>
      <Navbar />

      {/* ━━━━━━━━━━━━━━━━━━ HERO ━━━━━━━━━━━━━━━━━━ */}
      <section
        ref={heroRef}
        className="relative min-h-[90vh] flex items-center justify-center bg-surface overflow-hidden pt-24 pb-20"
      >
        {/* Neural network background */}
        <NeuralNetworkBg />

        {/* Floating chat bubbles */}
        {!prefersReducedMotion && <FloatingBubbles />}

        {/* Logo at top */}
        <motion.div
          className="absolute top-7 left-1/2 -translate-x-1/2 z-10"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link href="/">
            <Image
              src="/assets/devcubix-logo-dark.png"
              alt="Dev Cubix"
              width={280}
              height={114}
              className="h-16 w-auto object-contain dark:hidden block"
              priority
            />
            <Image
              src="/assets/devcubix-logo-white.png"
              alt="Dev Cubix"
              width={280}
              height={114}
              className="h-16 w-auto object-contain hidden dark:block"
              priority
            />
          </Link>
        </motion.div>

        {/* Animated gradient orbs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div
            className="absolute w-[600px] h-[600px] rounded-full blur-[140px] opacity-[0.15]"
            style={{ background: BRAND_RED, top: "5%", left: "-10%" }}
            animate={{ x: [0, 80, 0], y: [0, -50, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute w-[500px] h-[500px] rounded-full blur-[120px] opacity-[0.10]"
            style={{ background: "#4D65FF", top: "30%", right: "-8%" }}
            animate={{ x: [0, -60, 0], y: [0, 60, 0], scale: [1, 1.15, 1] }}
            transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute w-[400px] h-[400px] rounded-full blur-[100px] opacity-[0.08]"
            style={{ background: BRAND_RED, bottom: "0%", right: "20%" }}
            animate={{ x: [0, 40, -30, 0], y: [0, -40, 20, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        {/* Giant watermark */}
        <motion.span
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-[280px] sm:text-[400px] font-bold leading-none select-none pointer-events-none"
          style={{ color: `${BRAND_RED}08` }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2 }}
        >
          06
        </motion.span>

        {/* Hero content */}
        <motion.div
          className="relative z-10 max-w-[1100px] mx-auto px-6 text-center"
          style={{ opacity: heroOpacity, y: heroY }}
        >
          {/* Breadcrumb */}
          <motion.nav
            className="flex items-center justify-center gap-2 text-xs text-foreground/40 mb-8"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Link href="/" className="hover:text-foreground/70 transition-colors">Home</Link>
            <span>/</span>
            <Link href="/services" className="hover:text-foreground/70 transition-colors">Services</Link>
            <span>/</span>
            <span className="text-foreground/70">AI &amp; Chatbots</span>
          </motion.nav>

          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <p className="section-badge mb-6 mx-auto">Service 06</p>
          </motion.div>

          {/* Main heading */}
          <motion.h1
            className="text-3xl sm:text-5xl md:text-[64px] lg:text-[76px] font-bold leading-[1.08] font-ui mb-5 tracking-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            AI &amp; Chatbot
            <br />
            <span className="font-serif italic text-purple-light">
              Development
            </span>
          </motion.h1>

          {/* Tagline */}
          <motion.p
            className="text-foreground/50 text-lg md:text-xl max-w-2xl mx-auto font-body leading-relaxed mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.45 }}
          >
            Smart assistants that automate and delight. We build RAG-powered
            chatbots that understand your business, qualify leads, and support
            customers around the clock.
          </motion.p>

          {/* Tech badge */}
          <motion.div
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[#E11D24]/30 bg-[#E11D24]/5 mb-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <span className="w-2 h-2 rounded-full bg-[#E11D24] animate-pulse" />
            <span className="text-xs font-semibold text-badge-text tracking-wide">
              Powered by OpenAI, Supabase &amp; Custom RAG
            </span>
          </motion.div>

          {/* Stats */}
          <motion.div
            className="flex flex-wrap justify-center gap-8 sm:gap-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            {[
              { value: "$1,800+", label: "Starting Price" },
              { value: "4-6", label: "Weeks Timeline" },
              { value: "24/7", label: "Bot Uptime" },
            ].map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-red-accent">
                  {s.value}
                </p>
                <p className="text-xs text-foreground/40 mt-1 uppercase tracking-wider">
                  {s.label}
                </p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-surface to-transparent pointer-events-none" />
      </section>

      {/* ━━━━━━━━━━━━━━━━━━ WHAT WE NEED ━━━━━━━━━━━━━━━━━━ */}
      <section className="bg-surface py-20 sm:py-28 relative overflow-hidden">
        {/* Section glow */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] pointer-events-none"
          style={{
            background: `radial-gradient(ellipse, ${BRAND_RED}08 0%, transparent 70%)`,
          }}
        />

        <div className="relative z-10 max-w-[1100px] mx-auto px-6">
          <FadeInUp className="text-center mb-14">
            <p className="section-badge mb-4 mx-auto">Requirements</p>
            <h2 className="text-2xl sm:text-4xl md:text-[48px] font-bold font-ui leading-[1.1] mb-4">
              What We{" "}
              <span className="font-serif italic text-purple-light">Need</span>{" "}
              From You
            </h2>
            <p className="text-foreground/50 font-body text-lg max-w-xl mx-auto">
              The more context you give us, the smarter your chatbot will be from
              day one.
            </p>
          </FadeInUp>

          <StaggerChildren className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" staggerDelay={0.1}>
            {whatWeNeed.map((item, i) => (
              <StaggerItem key={item.item}>
                <div className="group relative rounded-2xl border border-card-border bg-surface-light p-6 h-full transition-all duration-500 hover:border-[#E11D24]/30 hover:shadow-lg hover:shadow-[#E11D24]/5">
                  {/* Number accent */}
                  <span
                    className="inline-flex items-center justify-center w-9 h-9 rounded-xl text-sm font-bold mb-4 transition-colors duration-300"
                    style={{
                      backgroundColor: `${BRAND_RED}12`,
                      color: BRAND_RED,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h4 className="text-base font-bold font-ui text-foreground mb-2">
                    {item.item}
                  </h4>
                  <p className="text-foreground/50 font-body text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━ PROCESS TIMELINE ━━━━━━━━━━━━━━━━━━ */}
      <section className="bg-surface py-20 sm:py-28 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute w-[500px] h-[500px] rounded-full blur-[120px] opacity-[0.06]"
            style={{ background: BRAND_RED, top: "20%", right: "-5%" }}
          />
        </div>

        <div className="relative z-10 max-w-[900px] mx-auto px-6">
          <FadeInUp className="text-center mb-16">
            <p className="section-badge mb-4 mx-auto">Process</p>
            <h2 className="text-2xl sm:text-4xl md:text-[48px] font-bold font-ui leading-[1.1] mb-4">
              How We{" "}
              <span className="font-serif italic text-purple-light">Build</span>{" "}
              It
            </h2>
            <p className="text-foreground/50 font-body text-lg max-w-xl mx-auto">
              A proven 6-week process from architecture to production deployment
              with monitoring.
            </p>
          </FadeInUp>

          <div className="max-w-lg mx-auto md:mx-0 md:ml-[20%]">
            {process.map((p, i) => (
              <TimelineStep
                key={p.step}
                step={p.step}
                desc={p.desc}
                time={p.time}
                index={i}
                isLast={i === process.length - 1}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━ CAPABILITIES ━━━━━━━━━━━━━━━━━━ */}
      <section className="bg-surface py-20 sm:py-28 relative overflow-hidden">
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] pointer-events-none"
          style={{
            background: `radial-gradient(ellipse, ${BRAND_RED}06 0%, transparent 70%)`,
          }}
        />

        <div className="relative z-10 max-w-[1100px] mx-auto px-6">
          <FadeInUp className="text-center mb-14">
            <p className="section-badge mb-4 mx-auto">Capabilities</p>
            <h2 className="text-2xl sm:text-4xl md:text-[48px] font-bold font-ui leading-[1.1] mb-4">
              What Your Bot{" "}
              <span className="font-serif italic text-purple-light">
                Can Do
              </span>
            </h2>
            <p className="text-foreground/50 font-body text-lg max-w-xl mx-auto">
              Not just a chatbot. A full AI system built on retrieval-augmented
              generation.
            </p>
          </FadeInUp>

          <StaggerChildren className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" staggerDelay={0.1}>
            {capabilities.map((cap) => (
              <StaggerItem key={cap.title}>
                <div className="group relative rounded-2xl border border-card-border bg-surface-light p-7 h-full transition-all duration-500 hover:border-[#E11D24]/30 hover:shadow-lg hover:shadow-[#E11D24]/5">
                  {/* Icon */}
                  <div
                    className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-5 transition-colors duration-300"
                    style={{
                      backgroundColor: `${BRAND_RED}10`,
                      color: BRAND_RED,
                    }}
                  >
                    {cap.icon}
                  </div>
                  <h4 className="text-base font-bold font-ui text-foreground mb-2">
                    {cap.title}
                  </h4>
                  <p className="text-foreground/50 font-body text-sm leading-relaxed">
                    {cap.desc}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </StaggerChildren>

          {/* Tech stack pills */}
          <FadeInUp className="mt-14" delay={0.2}>
            <div className="text-center mb-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-foreground/30">
                Built With
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2.5">
              {techStack.map((tech) => (
                <span
                  key={tech}
                  className="text-xs font-medium px-4 py-2 rounded-full border transition-colors duration-300"
                  style={{
                    borderColor: `${BRAND_RED}20`,
                    color: BRAND_RED,
                  }}
                >
                  {tech}
                </span>
              ))}
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━ DELIVERABLES + PRICING ━━━━━━━━━━━━━━━━━━ */}
      <section className="bg-surface py-20 sm:py-28 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute w-[600px] h-[600px] rounded-full blur-[140px] opacity-[0.08]"
            style={{ background: BRAND_RED, bottom: "0%", left: "-10%" }}
          />
        </div>

        <div className="relative z-10 max-w-[1100px] mx-auto px-6">
          <FadeInUp className="text-center mb-14">
            <p className="section-badge mb-4 mx-auto">Delivery</p>
            <h2 className="text-2xl sm:text-4xl md:text-[48px] font-bold font-ui leading-[1.1] mb-4">
              What You{" "}
              <span className="font-serif italic text-purple-light">Get</span>
            </h2>
          </FadeInUp>

          <div className="grid lg:grid-cols-5 gap-8 items-start">
            {/* Deliverables */}
            <FadeInUp className="lg:col-span-3" delay={0.1}>
              <div className="rounded-2xl border border-card-border bg-surface-light p-8">
                <h3 className="text-lg font-bold font-ui text-foreground mb-6">
                  Deliverables
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {deliverables.map((d, i) => (
                    <div key={d} className="flex items-start gap-3">
                      <span
                        className="flex-shrink-0 w-6 h-6 rounded-lg flex items-center justify-center text-[11px] font-bold mt-0.5"
                        style={{
                          backgroundColor: `${BRAND_RED}12`,
                          color: BRAND_RED,
                        }}
                      >
                        {i + 1}
                      </span>
                      <span className="text-sm text-foreground/70 font-body leading-relaxed">
                        {d}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeInUp>

            {/* Pricing card */}
            <ScaleIn className="lg:col-span-2" delay={0.2}>
              <div
                className="relative rounded-2xl border-2 p-8 overflow-hidden"
                style={{ borderColor: `${BRAND_RED}40` }}
              >
                {/* Glow */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `radial-gradient(ellipse at top, ${BRAND_RED}10 0%, transparent 70%)`,
                  }}
                />

                <div className="relative z-10">
                  <p
                    className="text-xs font-bold uppercase tracking-widest mb-2"
                    style={{ color: BRAND_RED }}
                  >
                    Starting At
                  </p>
                  <p className="text-5xl sm:text-6xl font-bold font-ui text-foreground mb-1">
                    $1,800
                  </p>
                  <p className="text-foreground/40 text-sm font-body mb-8">
                    Custom-scoped per project
                  </p>

                  <div className="space-y-4 mb-8">
                    {[
                      { label: "Payment Split", value: "40% / 30% / 30%" },
                      { label: "Timeline", value: "4-6 weeks" },
                      { label: "Revisions", value: "Iterative" },
                      { label: "Support", value: "Post-launch monitoring" },
                    ].map((row) => (
                      <div
                        key={row.label}
                        className="flex justify-between items-center py-2 border-b border-card-border"
                      >
                        <span className="text-sm text-foreground/50 font-body">
                          {row.label}
                        </span>
                        <span className="text-sm font-semibold text-foreground">
                          {row.value}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="text-center text-xs text-foreground/40 font-body">
                    <p>40% upfront to start</p>
                    <p>30% at beta delivery</p>
                    <p>30% on production deploy</p>
                  </div>
                </div>
              </div>
            </ScaleIn>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━━━━ CTA ━━━━━━━━━━━━━━━━━━ */}
      <section className="py-24 md:py-32 bg-surface text-center relative overflow-hidden">
        {/* Background orb */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full pointer-events-none"
          style={{
            background: `radial-gradient(circle, ${BRAND_RED}0A 0%, transparent 70%)`,
          }}
        />

        <FadeInUp className="relative z-10 max-w-2xl mx-auto px-6">
          <p className="section-badge mb-6 mx-auto">Ready to automate?</p>

          <h2 className="text-2xl sm:text-4xl md:text-[52px] font-bold font-ui leading-[1.1] mb-5">
            Let&apos;s Build Your{" "}
            <span className="font-serif italic text-purple-light">
              AI Assistant
            </span>
          </h2>

          <p className="text-foreground/50 mb-10 font-body text-lg leading-relaxed">
            Whether you need a lead qualification bot, a customer support agent,
            or a full RAG-powered knowledge system, we will scope it, build it,
            and deploy it.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <span className="relative inline-block rounded-full p-[2px] gradient-btn-bg">
              <Link
                href="/contact"
                className="relative z-10 inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-semibold text-sm hover:opacity-90 transition-opacity"
                style={{
                  background:
                    "linear-gradient(rgb(180, 20, 28) 25%, rgb(225, 29, 36))",
                }}
              >
                Start a Project
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </Link>
            </span>

            <button
              type="button"
              className="text-sm font-medium text-foreground/40 hover:text-foreground/70 transition-colors inline-flex items-center gap-2"
              onClick={() => {
                // Attempt to open the chatbot widget
                const chatBtn = document.querySelector(
                  "[data-chat-trigger]"
                ) as HTMLButtonElement | null;
                if (chatBtn) chatBtn.click();
              }}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"
                />
              </svg>
              See our AI chatbot in action
            </button>
          </div>
        </FadeInUp>
      </section>

      <Footer />
    </>
  );
}
